import type {
  ConditionalRuleDto,
  ConditionalRulesDto,
  ShopOverrideRuleActionDto,
  ShopOverrideRuleDto,
} from "@babackapp/private-api-client/types";
import type { OverrideRuleFormValues } from "./validation";
import { FieldType } from "./field-definitions";

export type ActionParameterType =
  | FieldType
  | "TIME_DELAY"
  | "ACTIONABLE_POSITIVE_NUMBER"
  | "NONE";
export type ActionParameterTypeTimeDelayModes = "relativeDelay" | "fixedDate";

export const ACTION_TYPES: {
  type: ShopOverrideRuleActionDto["type"];
  label: string;
  description: string;
  parameterType: ActionParameterType;
  parameterSuffix?: string;
}[] = [
  {
    type: "OVERRIDE_AUTO_TREATMENT",
    label: "Override Auto Treatment",
    description: "Override automatic return treatment decision",
    parameterType: "BOOLEAN",
  },
  {
    type: "OVERRIDE_RETURN_DELAY",
    label: "Override Return Delay",
    description: "Override the return delay period",
    parameterType: "TIME_DELAY",
    parameterSuffix: "days",
  },
  {
    type: "OVERRIDE_SHIPPING_REFUND",
    label: "Override Shipping Refund",
    description: "Override shipping refund rules",
    parameterType: "STRING",
  },
  {
    type: "OVERRIDE_INITIAL_SHIPPING_AMOUNT",
    label: "Override Initial Shipping Refund Amount",
    description: "Override initial shipping refund amount rules",
    parameterType: "ACTIONABLE_POSITIVE_NUMBER",
  },
  {
    type: "OVERRIDE_STORE_RETURN",
    label: "Override Store Return",
    description: "Override store return availability",
    parameterType: "BOOLEAN",
  },
  {
    type: "OVERRIDE_RESHIPPING_PAID",
    label: "Override Reshipping Paid",
    description: "Override reshipping payment rules",
    parameterType: "BOOLEAN",
  },
  {
    type: "OVERRIDE_CREDIT_ENABLED",
    label: "Override Credit Enabled",
    description: "Override credit availability",
    parameterType: "BOOLEAN",
  },
  {
    type: "OVERRIDE_NON_RETURNABLE",
    label: "Override Non-Returnable",
    description:
      "Force specific items to be non-returnable based on conditions",
    parameterType: "NONE",
  },
  {
    type: "OVERRIDE_RETURN_FEE",
    label: "Override Return Fee",
    description: "Set the return fee amount charged to the customer",
    parameterType: "ACTIONABLE_POSITIVE_NUMBER",
  },
  {
    type: "OVERRIDE_CREDIT_RETURN_FEE",
    label: "Override Credit Return Fee",
    description:
      "Override return fee applicability for store credit (avoir) resolutions",
    parameterType: "BOOLEAN",
  },
] as const;

export const getActionTypeInfo = (
  actionType: ShopOverrideRuleActionDto["type"],
) => {
  return ACTION_TYPES.find((action) => action.type === actionType);
};

export const generateUniqueId = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

export const createEmptyCondition = () =>
  ({
    id: "",
    type: "STRING" as const,
    operator: undefined,
    value: "",
  }) as unknown as ConditionalRuleDto;

export const createDefaultConditions = (): ConditionalRulesDto => ({
  operator: "AND",
  rules: [createEmptyCondition() as unknown as ConditionalRuleDto],
});

export const createDefaultAction = (
  actionType?: ShopOverrideRuleActionDto["type"],
): ShopOverrideRuleActionDto => {
  const type = actionType || "OVERRIDE_AUTO_TREATMENT";
  const actionInfo = getActionTypeInfo(type);

  let defaultValue: unknown = false;
  if (actionInfo?.parameterType === "NUMBER") {
    defaultValue = 30;
  }

  if (actionInfo?.parameterType === "ACTIONABLE_POSITIVE_NUMBER") {
    defaultValue = -1;
  }

  if (actionInfo?.parameterType === "NONE") {
    defaultValue = true;
  }

  return {
    type,
    parameters: { value: defaultValue },
  };
};

export const convertRuleToFormValues = (
  rule: ShopOverrideRuleDto,
): OverrideRuleFormValues => {
  // Ensure conditions are properly structured
  let conditions: ConditionalRulesDto;

  if (rule.conditions && typeof rule.conditions === "object") {
    // If conditions exist, use them directly (they should already be in the right format)
    conditions = rule.conditions as ConditionalRulesDto;

    // Ensure we have the required structure
    if (!conditions.operator) {
      conditions.operator = "AND";
    }
    if (!Array.isArray(conditions.rules)) {
      conditions.rules = [];
    }

    // Ensure each rule has the required fields
    conditions.rules = conditions.rules.map((rule) => ({
      id: rule.id || "",
      type: rule.type || "STRING",
      operator: rule.operator || "EQ",
      value: rule.value,
    }));

    // If no rules exist, add a default one
    if (conditions.rules.length === 0) {
      conditions.rules = [createEmptyCondition()];
    }
  } else {
    // Fallback to default conditions
    conditions = createDefaultConditions();
  }

  return {
    name: rule.name,
    description: rule.description || "",
    isActive: rule.isActive,
    priority: rule.priority,
    conditions,
    action: rule.action,
  };
};
